import { useCallback, useEffect, useRef, useState } from 'react';
import './UserDashboard.css';
import UserPortal from '../../../auth/pages/UserPortal';
import { useDispatch, useSelector } from 'react-redux';
import { setScanContextField, setSessionDetailsField } from '../../../../app/providers/store';
import {
  PROFILE_USERS_BROWSER_LAUNCH,
  QUEUE_CONFIRM,
  QUEUE_DETAILS,
  QUEUE_JOIN
} from '../../../../shared/constants/endpoints';

const UserDashboard = ({ request, pushActivity }) => {
  const dispatch = useDispatch();
  const scanContext = useSelector((state) => state.user.scanContext);
  const sessionDetails = useSelector((state) => state.user.sessionDetails);
  const [activeQueue, setActiveQueue] = useState(null);
  const [activeQueueStatus, setActiveQueueStatus] = useState({ loading: false, error: '' });
  const [confirmStatus, setConfirmStatus] = useState({ state: 'idle', message: '' });
  const pollIntervalRef = useRef(null);

  const handleScanContextFieldChange = (field, value) => {
    dispatch(setScanContextField({ field, value }));
  };

  const handleSessionDetailsFieldChange = (field, value) => {
    dispatch(setSessionDetailsField({ field, value }));
  };

  const handleLaunchSession = async () => {
    try {
      const payload = {
        mallUuid: scanContext.mallUuid,
        storeUuid: scanContext.storeUuid,
        sectionUuid: scanContext.sectionUuid,
        floorNumber: scanContext.floorNumber ? Number(scanContext.floorNumber) : null,
        deviceUuid: scanContext.deviceUuid,
        isBrowserLaunch: true,
        browserType: 'Browser',
        browserVersion: navigator.userAgent,
        osType: navigator.platform || 'Unknown',
        osVersion: '',
        ipAddress: ''
      };
      const data = await request(PROFILE_USERS_BROWSER_LAUNCH, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const sessionId = data?.data?.data?.session?.sessionId || data?.data?.session?.sessionId || '';
      if (sessionId) {
        dispatch(setSessionDetailsField({ field: 'sessionId', value: sessionId }));
      }
      pushActivity('Session launched', data);
    } catch (error) {
      pushActivity('Session launch failed', error.data || { status: error.status });
    }
  };

  const fetchActiveQueue = useCallback(async (queueUuid, { silent = false } = {}) => {
    if (!queueUuid) {
      setActiveQueue(null);
      setActiveQueueStatus({ loading: false, error: '' });
      setConfirmStatus({ state: 'idle', message: '' });
      return null;
    }
    if (!silent) {
      setActiveQueueStatus({ loading: true, error: '' });
    }
    try {
      const data = await request(QUEUE_DETAILS(queueUuid), {
        method: 'GET',
        headers: {
          sessionId: sessionDetails.sessionId
        }
      });
      const queueData = data?.data?.data || data?.data || null;
      setActiveQueue(queueData);
      if (queueData?.status === 'serving') {
        setConfirmStatus({
          state: 'success',
          message: 'Presence confirmed. You are now active at the counter.'
        });
      } else if (queueData?.canConfirmPresence) {
        setConfirmStatus({ state: 'idle', message: '' });
      }
      setActiveQueueStatus({ loading: false, error: '' });
      return queueData;
    } catch (error) {
      const status = error?.status || error?.data?.status;
      if (status === 404) {
        dispatch(setSessionDetailsField({ field: 'queueUuid', value: '' }));
        setActiveQueue(null);
        setActiveQueueStatus({ loading: false, error: '' });
        return null;
      }
      setActiveQueueStatus({
        loading: false,
        error: 'Unable to load active queue right now.'
      });
      return null;
    }
  }, [dispatch, request, sessionDetails.sessionId]);

  const handleJoinQueue = async () => {
    try {
      const payload = {
        storeUuid: scanContext.storeUuid,
        sectionUuid: sessionDetails.sectionUuid || scanContext.sectionUuid,
        sessionId: sessionDetails.sessionId,
        phone: sessionDetails.phone,
        deviceUuid: scanContext.deviceUuid,
        isBrowserLaunch: true
      };
      const data = await request(QUEUE_JOIN, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const queueData = data?.data?.data || data?.data || {};
      const queueUuid = queueData?.queueUuid || '';
      if (queueUuid) {
        dispatch(setSessionDetailsField({ field: 'queueUuid', value: queueUuid }));
        setConfirmStatus({ state: 'idle', message: '' });
        await fetchActiveQueue(queueUuid);
      }
      pushActivity('Joined queue', data);
    } catch (error) {
      pushActivity('Join queue failed', error.data || { status: error.status });
    }
  };

  const handleRefreshActiveQueue = async () => {
    await fetchActiveQueue(sessionDetails.queueUuid);
  };

  const handleConfirmPresence = async () => {
    if (!sessionDetails.queueUuid) {
      return;
    }
    try {
      setConfirmStatus({ state: 'submitting', message: '' });
      setActiveQueueStatus({ loading: true, error: '' });
      const data = await request(QUEUE_CONFIRM(sessionDetails.queueUuid), {
        method: 'POST',
        headers: {
          sessionId: sessionDetails.sessionId
        }
      });
      pushActivity('Confirmed presence', data);
      setConfirmStatus({
        state: 'success',
        message: 'Presence confirmed. Syncing your live queue state now.'
      });
      await fetchActiveQueue(sessionDetails.queueUuid);
    } catch (error) {
      setActiveQueueStatus({
        loading: false,
        error: 'Unable to confirm presence right now.'
      });
      setConfirmStatus({
        state: 'error',
        message: 'Confirm presence failed. Please try again or show your OTP at the counter.'
      });
      pushActivity('Confirm presence failed', error.data || { status: error.status });
    }
  };

  useEffect(() => {
    if (!sessionDetails.queueUuid) {
      setActiveQueue(null);
      setActiveQueueStatus({ loading: false, error: '' });
      setConfirmStatus({ state: 'idle', message: '' });
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return undefined;
    }

    fetchActiveQueue(sessionDetails.queueUuid);

    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
    }
    pollIntervalRef.current = window.setInterval(() => {
      fetchActiveQueue(sessionDetails.queueUuid, { silent: true });
    }, 30000);

    return () => {
      if (pollIntervalRef.current) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchActiveQueue, sessionDetails.queueUuid]);

  return (
    <UserPortal
      scanContext={scanContext}
      sessionDetails={sessionDetails}
      onScanContextFieldChange={handleScanContextFieldChange}
      onSessionDetailsFieldChange={handleSessionDetailsFieldChange}
      onLaunchSession={handleLaunchSession}
      onJoinQueue={handleJoinQueue}
      activeQueue={activeQueue}
      activeQueueStatus={activeQueueStatus}
      confirmStatus={confirmStatus}
      onRefreshActiveQueue={handleRefreshActiveQueue}
      onConfirmPresence={handleConfirmPresence}
    />
  );
};

export default UserDashboard;
